import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LuBuilding,
  LuCalendar,
  LuCheck,
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuClock,
  LuCopy,
  LuLink,
  LuLoader,
  LuMail,
  LuVideo,
  LuX
} from "react-icons/lu";
import CustomConfirmModal from "../../../components/Common/CustomConfirmModal";
import { MARKETING_BASE_URL } from "../../../constants";
import { apiGet, apiPost, toastError, toastSuccess } from "../../../utils";
import RescheduleModal from "./RescheduleModal";
import ViewAllBookingsModal from "./ViewAllBookingsModal";

export default function DemoBookings({ userId }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingData, setFetchingData] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const [viewMonth, setViewMonth] = useState(new Date());

  const todayStr = useMemo(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  }, []);

  const [selectedDateStr, setSelectedDateStr] = useState(todayStr);

  const fetchBookings = useCallback(async (currentViewMonth) => {
    try {
      setFetchingData(true);

      const year = currentViewMonth.getFullYear();
      const month = currentViewMonth.getMonth();

      // We want to fetch from the start of the visible calendar (which might be late prev month)
      // to the end of the visible calendar (which might be early next month)
      // A simple safe buffer is to fetch from start of prev month to end of next month
      const from = new Date(year, month - 1, 1).toISOString().split("T")[0];
      const to = new Date(year, month + 2, 0).toISOString().split("T")[0];

      const res = await apiGet(
        `/api/knowledge-partner/demo-bookings?from=${from}&to=${to}`
      );
      if (res?.data) {
        setBookings(res.data);
      }
    } catch (err) {
      toastError(err?.message || "Failed to load bookings");
    } finally {
      setLoading(false);
      setFetchingData(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings(viewMonth);
  }, [fetchBookings, viewMonth]);

  const handleCancel = async () => {
    if (!confirmCancel) return;
    try {
      setCancellingId(confirmCancel.eventId);
      await apiPost(
        `/api/knowledge-partner/demo-bookings/${confirmCancel.eventId}/cancel`
      );
      toastSuccess("Demo booking cancelled. Attendees have been notified.");
      setConfirmCancel(null);
      fetchBookings(viewMonth);
    } catch (err) {
      toastError(err?.message || "Failed to cancel booking");
    } finally {
      setCancellingId(null);
    }
  };

  const copyBookingLink = () => {
    const link = `${MARKETING_BASE_URL}/knowledge-partners/${userId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // --- Calendar Logic ---
  const todayDate = new Date();

  const prevMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  };
  const goToCurrentMonth = () => {
    setViewMonth(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));
  };

  const isCurrentMonthOrPast =
    viewMonth.getFullYear() < todayDate.getFullYear() ||
    (viewMonth.getFullYear() === todayDate.getFullYear() && viewMonth.getMonth() <= todayDate.getMonth());

  const calendarDays = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days = [];

    // Previous month days
    const startDayOfWeek = firstDay.getDay(); // 0-6 (Sun-Sat)
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({
        date: d,
        isCurrentMonth: false,
        dateStr: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
      });
    }

    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const d = new Date(year, month, i);
      days.push({
        date: d,
        isCurrentMonth: true,
        dateStr: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
      });
    }

    // Next month days to complete 6 rows (42 cells)
    let nextDay = 1;
    while (days.length < 42) {
      const d = new Date(year, month + 1, nextDay++);
      days.push({
        date: d,
        isCurrentMonth: false,
        dateStr: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
      });
    }

    return days;
  }, [viewMonth]);

  const getBookingsForDate = useCallback((dateStr) => {
    return bookings.filter((b) => {
      const bookingDate = moment(b.start).format("YYYY-MM-DD");
      return bookingDate === dateStr;
    });
  }, [bookings]);

  const selectedDateBookings = useMemo(() => {
    return getBookingsForDate(selectedDateStr).sort(
      (a, b) => new Date(a.start) - new Date(b.start)
    );
  }, [selectedDateStr, getBookingsForDate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <LuLoader className="w-8 h-8 text-[#313C4C] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Top Banner: Booking Link */}
      <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-none flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-none bg-white border border-slate-200 flex items-center justify-center shadow-2xs">
            <LuLink className="w-4 h-4 text-[#313C4C]" />
          </div>
          <div>
            <p className="text-[11px] font-medium text-slate-900 mb-0.5 uppercase tracking-wider">Your Demo Booking Link</p>
            <p className="text-[11px] text-slate-500 font-medium truncate max-w-[250px] sm:max-w-md">
              {MARKETING_BASE_URL}/knowledge-partners/{userId}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={copyBookingLink}
          className="flex items-center justify-center w-full sm:w-auto gap-1.5 px-3 py-1.5 bg-[#313C4C] text-white rounded-none text-[11px] font-medium uppercase tracking-wider hover:bg-[#1e293b] transition-all shadow-2xs"
        >
          {copied ? <LuCheck className="w-3 h-3" /> : <LuCopy className="w-3 h-3" />}
          {copied ? "Copied!" : "Copy Link"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

        {/* Left Column: Big Interactive Calendar */}
        <div className="lg:col-span-7 xl:col-span-8 relative">
          <div className="bg-white rounded-none border border-slate-200 p-3 sm:p-4 shadow-2xs relative overflow-hidden">

            {fetchingData && (
              <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[1px] flex items-center justify-center rounded-none">
                <div className="flex flex-col items-center gap-1.5">
                  <LuLoader className="w-6 h-6 text-[#313C4C] animate-spin" />
                  <span className="text-[11px] font-medium text-[#313C4C] uppercase tracking-wider">Fetching data...</span>
                </div>
              </div>
            )}

            {/* Calendar Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <select
                    value={viewMonth.getMonth()}
                    onChange={(e) => setViewMonth(new Date(viewMonth.getFullYear(), parseInt(e.target.value), 1))}
                    className="appearance-none bg-slate-50 border border-slate-200 rounded-none pl-3 pr-8 py-1 text-[11px] font-medium text-slate-800 hover:bg-slate-100 transition-all focus:outline-none focus:border-[#313C4C] cursor-pointer shadow-2xs"
                  >
                    {Array.from({ length: 12 }).map((_, i) => (
                      <option key={i} value={i} disabled={viewMonth.getFullYear() === todayDate.getFullYear() && i < todayDate.getMonth()}>
                        {new Date(0, i).toLocaleString("default", { month: "long" })}
                      </option>
                    ))}
                  </select>
                  <LuChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                </div>

                <div className="relative">
                  <select
                    value={viewMonth.getFullYear()}
                    onChange={(e) => {
                      const newYear = parseInt(e.target.value);
                      let newMonth = viewMonth.getMonth();
                      if (newYear === todayDate.getFullYear() && newMonth < todayDate.getMonth()) {
                        newMonth = todayDate.getMonth();
                      }
                      setViewMonth(new Date(newYear, newMonth, 1));
                    }}
                    className="appearance-none bg-slate-50 border border-slate-200 rounded-none pl-3 pr-8 py-1 text-[11px] font-medium text-slate-800 hover:bg-slate-100 transition-all focus:outline-none focus:border-[#313C4C] cursor-pointer shadow-2xs"
                  >
                    {Array.from({ length: 5 }).map((_, i) => {
                      const year = todayDate.getFullYear() + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                  <LuChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={goToCurrentMonth}
                  className="px-2.5 py-1 text-[10px] font-medium text-[#313C4C] bg-[#313C4C]/10 hover:bg-[#313C4C]/20 rounded-none transition-colors mr-1 uppercase tracking-wider"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={prevMonth}
                  disabled={isCurrentMonthOrPast}
                  className={`p-1 rounded-none transition-colors border border-transparent 
                    ${isCurrentMonthOrPast ? "text-slate-300 cursor-not-allowed" : "text-slate-600 hover:bg-slate-100 hover:border-slate-200"}`}
                >
                  <LuChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={nextMonth}
                  className="p-1 hover:bg-slate-100 text-slate-600 rounded-none transition-colors border border-transparent hover:border-slate-200"
                >
                  <LuChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>


            <div className="grid grid-cols-7 gap-1 mb-1">
              {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => (
                <div key={d} className="text-[10px] font-medium text-slate-500 text-center tracking-wider uppercase">
                  {d}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((dayObj, idx) => {
                const dayBookings = getBookingsForDate(dayObj.dateStr);
                const hasBookings = dayBookings.length > 0;
                const isSelected = selectedDateStr === dayObj.dateStr;
                const isToday = dayObj.dateStr === todayStr;

                return (
                  <button
                    key={`${dayObj.dateStr}-${idx}`}
                    type="button"
                    onClick={() => {
                      setSelectedDateStr(dayObj.dateStr);
                      if (!dayObj.isCurrentMonth) {
                        setViewMonth(new Date(dayObj.year, dayObj.month, 1));
                      }
                    }}
                    className={`
                      relative flex flex-col items-center justify-center min-h-[38px] py-2 rounded-none transition-all border
                      ${isSelected ? "border-[#313C4C] bg-[#313C4C] text-white shadow-2xs" : "border-slate-200/60"}
                      ${!isSelected && dayObj.isCurrentMonth && !isToday ? "bg-slate-50 hover:bg-slate-100 text-slate-800" : ""}
                      ${!isSelected && !dayObj.isCurrentMonth ? "bg-transparent text-slate-300 hover:bg-slate-50" : ""}
                      ${isToday && !isSelected ? "border-[#313C4C] bg-[#313C4C]/5 text-[#313C4C]" : ""}
                    `}
                  >
                    <span className={`text-[11px] font-medium ${isToday && !isSelected ? "text-[#313C4C]" : ""}`}>
                      {dayObj.date.getDate()}
                    </span>

                    {/* Booking indicator dots */}
                    {hasBookings && (
                      <div className="absolute bottom-1 flex gap-0.5">
                        {dayBookings.slice(0, 3).map((_, i) => (
                          <div
                            key={i}
                            className={`w-1 h-1 rounded-none ${isSelected ? "bg-white" : "bg-[#313C4C]"}`}
                          />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column: Selected Date Bookings */}
        <div className="lg:col-span-5 xl:col-span-4 flex flex-col">
          <div className="bg-slate-50 border border-slate-200 rounded-none p-3.5 lg:col-span-5 xl:col-span-4 h-fit shadow-2xs">
            <div className="flex justify-between items-start mb-3 pb-2.5 border-b border-slate-200">
              <div>
                <h4 className="text-[11px] font-medium text-slate-900 uppercase tracking-wider">
                  {moment(selectedDateStr).format("dddd, MMMM D")}
                </h4>
                <p className="text-[10px] text-slate-500 font-medium">
                  {selectedDateBookings.length} {selectedDateBookings.length === 1 ? "demo" : "demos"} scheduled
                </p>
              </div>
              <button
                type="button"
                onClick={() => setViewAllOpen(true)}
                className="text-[10px] font-medium text-[#313C4C] hover:underline px-1.5 py-0.5 rounded-none uppercase tracking-wider shrink-0"
              >
                View All
              </button>
            </div>

            {fetchingData ? (
              <div className="flex flex-col items-center justify-center py-8 text-center bg-white rounded-none border border-slate-200 h-[240px]">
                <LuLoader className="w-6 h-6 text-[#313C4C] animate-spin mb-2" />
                <h5 className="font-medium text-[11px] text-slate-700 uppercase tracking-wider">Loading bookings...</h5>
              </div>
            ) : selectedDateBookings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center bg-white rounded-none border border-slate-200 h-[240px]">
                <div className="w-10 h-10 bg-slate-50 rounded-none border border-slate-200 flex items-center justify-center mb-2">
                  <LuCalendar className="w-5 h-5 text-slate-400" />
                </div>
                <h5 className="font-medium text-[11px] text-slate-700 uppercase tracking-wider">No demos on this day</h5>
                <p className="text-[11px] text-slate-500 mt-0.5 max-w-[180px] font-medium">
                  Check another date for upcoming meetings.
                </p>
              </div>
            ) : (
              <div className="space-y-2.5 max-h-[460px] overflow-y-auto pr-1 scroll-thin">
                {selectedDateBookings.slice(0, 5).map((booking) => {
                  const isPast = new Date(booking.end) < new Date();

                  return (
                    <div
                      key={booking.eventId}
                      className={`p-2.5 bg-white border rounded-none transition-all shadow-2xs
                        ${isPast ? "border-slate-200 opacity-75" : "border-slate-200 hover:border-slate-300"}
                      `}
                    >
                      {/* Time Header */}
                      <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-slate-100">
                        <div className="w-1.5 h-1.5 rounded-none bg-[#313C4C]" />
                        <span className="text-[11px] font-medium text-[#313C4C]">
                          {moment(booking.start).format("h:mm A")}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">
                          to {moment(booking.end).format("h:mm A")}
                        </span>
                        {isPast && (
                          <span className="ml-auto text-[9px] font-medium uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-none border border-slate-200">
                            Completed
                          </span>
                        )}
                      </div>

                      {/* Attendee Info */}
                      <div className="mb-2 space-y-0.5">
                        <p className="font-medium text-slate-900 text-[11px]">
                          {booking.requesterName || "Unknown Attendee"}
                        </p>
                        <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-medium">
                          <LuMail className="w-3 h-3 text-slate-400" />
                          {booking.requesterEmail}
                        </div>
                        {booking.requesterCompany && (
                          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-medium">
                            <LuBuilding className="w-3 h-3 text-slate-400" />
                            {booking.requesterCompany}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-1.5 mt-2">
                        {booking.meetLink && (
                          <a
                            href={booking.meetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`w-full flex items-center justify-center gap-1.5 px-2.5 py-1 rounded-none text-[10px] font-medium uppercase tracking-wider transition-colors
                              ${isPast
                                ? "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
                                : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-300"
                              }
                            `}
                          >
                            <LuVideo className="w-3 h-3" />
                            Join Meet
                          </a>
                        )}

                        {!isPast && (
                          <div className="grid grid-cols-2 gap-1.5">
                            <button
                              type="button"
                              onClick={() => setRescheduleBooking(booking)}
                              className="flex items-center justify-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-none text-[10px] font-medium uppercase tracking-wider hover:bg-slate-200 border border-slate-200 transition-colors"
                            >
                              <LuClock className="w-3 h-3" />
                              Reschedule
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmCancel(booking)}
                              disabled={cancellingId === booking.eventId}
                              className="flex items-center justify-center gap-1 px-2 py-1 bg-rose-50 text-rose-700 rounded-none text-[10px] font-medium uppercase tracking-wider hover:bg-rose-100 border border-rose-200 transition-colors disabled:opacity-50"
                            >
                              {cancellingId === booking.eventId ? (
                                <LuLoader className="w-3 h-3 animate-spin" />
                              ) : (
                                <LuX className="w-3 h-3" />
                              )}
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      <CustomConfirmModal
        open={!!confirmCancel}
        handleClose={() => setConfirmCancel(null)}
        title="Cancel Demo Booking"
        message={
          confirmCancel
            ? `Are you sure you want to cancel the demo with ${confirmCancel.requesterName}? Both you and the attendee will be notified.`
            : ""
        }
        onSubmitConfirm={handleCancel}
        confirmText="Cancel Demo"
      />

      <RescheduleModal
        open={!!rescheduleBooking}
        handleClose={() => setRescheduleBooking(null)}
        booking={rescheduleBooking}
        userId={userId}
        onSuccess={() => fetchBookings(viewMonth)}
      />

      <ViewAllBookingsModal
        open={viewAllOpen}
        handleClose={() => setViewAllOpen(false)}
        bookings={selectedDateBookings}
        selectedDateStr={selectedDateStr}
        setRescheduleBooking={setRescheduleBooking}
        setConfirmCancel={setConfirmCancel}
        cancellingId={cancellingId}
      />
    </div>
  );
}
